import "./slider.css";
import { routes } from "../../routes";
import { useNavigate } from "react-router-dom";
import CommonModal from "../CommonModal/CommonModal";
import GameBox from "../../assets/images/gameBox.png";
import { useDispatch, useSelector } from "react-redux";
import LuxryCar from "../../assets/images/slider3.png";
import ItsTimeBox from "../../assets/images/itsBox.png";
import VsBattle from "../../assets/images/fireSlide1.png";
import React, { useRef, useState, useEffect } from "react";
import BonanzaBox from "../../assets/images/bonanzaBox.png";
import { trackClaimNowClick } from "../../utils/GoogleTagTracking";
import { toggleOnboard } from "../../redux/slices/onBoardingSlice";

const TOTAL_SLIDES = 3;

function Slider() {
  const sliderRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeSlide, setActiveSlide] = useState(0);
  const user = useSelector((state) => state?.auth?.user);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const totalBoxes = useSelector((state) => state.box.totalBoxes);
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);
  const isLoggedIn = useSelector((state) => state?.auth?.isLoggedIn);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 992);

  /* ── Detect mobile breakpoint ── */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const claimMoreBoxes = () => {
    if (user?.free_boxes && user?.free_boxes_remaining > 0) {
      navigate(`${routes.ACCOUNT}/${routes.ACCOUNT_FREEBOXES}`);
    } else setShowClaimModal(true);
  };

  /* ── Track active slide via IntersectionObserver ── */
  useEffect(() => {
    if (!isMobile || !sliderRef.current) return;

    const container = sliderRef.current;
    const cards = Array.from(container.querySelectorAll(".slider-card"));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const idx = cards.indexOf(entry.target);
            if (idx !== -1) setActiveSlide(idx);
          }
        });
      },
      { root: container, threshold: 0.6 },
    );

    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [isMobile]);

  /* ── Scroll to a specific slide (dot click) ── */
  const scrollToSlide = (index) => {
    if (!sliderRef.current) return;
    const cards = sliderRef.current.querySelectorAll(".slider-card");
    if (cards[index]) {
      cards[index].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
    }
  };

  return (
    <div className="slider-wrapper">
      <div className="slider-container" ref={sliderRef}>
        {/* ── LEFT CARD: Battles ── */}
        <div className="slider-card card-battles">
          <span className="card-badge">{totalBoxes} Boxes</span>
          <div className="card-content battles-content">
            <div className="battles-left">
              <img src={VsBattle} alt="VS Battle" className="battles-img" />
            </div>
            <div className="battles-right">
              <h2 className="card-titles text-akira">
                BATTLES
                <br />
                ARE LIVE
              </h2>
              <p className="card-descriptions text-AktivGrotesk-Medium">
                Go head-to-head.
                <br />
                Best unbox wins.
              </p>
              <button
                onClick={() => navigate(routes.BATTLES)}
                className="btn-slider"
              >
                Play Now{" "}
                <span>
                  <svg
                    style={{ marginBottom: "3px" }}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="black"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 4 14 12 6 20" />
                    <polyline points="12 4 20 12 12 20" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* ── CENTER CARD: Sign Up ── */}
        <div className="slider-card card-signup">
          <div
            onClick={() => {
              if (!isLoggedIn) {
                dispatch(toggleOnboard({ login: true }));
              } else {
                trackClaimNowClick();
                claimMoreBoxes(isDarkMode);
              }
            }}
            className="card-content card-content-center cursor-pointer"
          >
            <h3 className="signup-subtitle text-akira">SIGN UP TODAY &amp;</h3>
            <h1 className="signup-title d-center flex-column text-akira">
              <span className="text-white">OPEN YOUR</span>
              <span>3 FREE BOXES</span>
            </h1>
            <div className="signup-image">
              <img
                src={BonanzaBox}
                alt="Budget Bonanza Box"
                className="signup-img signup-img-left"
              />
              <img
                src={GameBox}
                alt="Game Box"
                className="signup-img signup-img-center"
              />
              <img
                src={ItsTimeBox}
                alt="Its Time Box"
                className="signup-img signup-img-right"
              />
            </div>
          </div>
        </div>

        {/* ── RIGHT CARD: Luxury ── */}
        <div className="slider-card card-luxury">
          <span className="card-badge">{totalBoxes} Boxes</span>
          <div className="card-content card-content-luxury">
            <div className="luxury-text">
              <h2 className="card-titles text-akira">
                UNBOX
                <br />
                LUXURY ITEMS
              </h2>
              <p className="card-descriptions card-descriptions-space text-AktivGrotesk-Medium">
                Experience the thrill of premium wins.
              </p>
              <button
                onClick={() => navigate(routes.UNBOXES)}
                className="btn-slider"
              >
                Unbox Now
                <span>
                  <svg
                    style={{ marginBottom: "3px" }}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="black"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 4 14 12 6 20" />
                    <polyline points="12 4 20 12 12 20" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
          <div className="luxury-image-wrapper">
            <img src={LuxryCar} alt="Luxury Car" className="luxury-img" />
          </div>
        </div>
      </div>

      {/* ── Dots (mobile only) ── */}
      {isMobile && (
        <div className="slider-dots">
          {Array.from({ length: TOTAL_SLIDES }).map((_, i) => (
            <button
              key={i}
              className={`slider-dot${activeSlide === i ? " active" : ""}`}
              onClick={() => scrollToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      <CommonModal
        show={showClaimModal}
        onClose={() => setShowClaimModal(false)}
        onConfirm={() => {
          setShowClaimModal(false);
          navigate("/unboxing");
        }}
        title={
          <h4
            className={`${isDarkMode ? "text-light-lavender-purple" : "text-deep-purple"} w-100 d-center`}
          >
            Claim More Boxes
          </h4>
        }
        body={
          <>
            <p
              className={`${isDarkMode ? "text-muter-lavender-purple" : "text-charcoal-gray"} mb-2`}
            >
              You have already redeemed this offer. You can play more boxes from
              here.
            </p>
            <h6
              className={`${isDarkMode ? "text-muter-lavender-purple" : "text-charcoal-gray"} mt-2`}
            >
              Click here to get boxes!
            </h6>
          </>
        }
        confirmText="View Boxes"
        isDarkMode={isDarkMode}
        showFooter={true}
        btnStyle={
          isDarkMode
            ? "filled-medium-deep-purple-btn-dark"
            : "filled-medium-deep-purple-btn-light"
        }
      />
    </div>
  );
}

export default Slider;
